import {
  type BigDecimal,
  absoluteValue,
  createBigDecimal,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { hyperliquidAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { parseHyperliquidActionEvidence } from '../parseActionEvidence'
import { fetchSpotMetaInfo } from './fetchSpotMetaInfo'
import { responseSchema } from './schema'

const spotDirs = new Set(['Buy', 'Sell', 'Spot Dust Conversion'])
const perpDirs = new Set([
  'Open Long',
  'Open Long (Price Improved)',
  'Open Short',
  'Open Short (Price Improved)',
  'Close Long',
  'Close Short',
  'Close Long (Price Improved)',
  'Close Short (Price Improved)',
  'Long > Short',
  'Short > Long',
  'Market Order Liquidation: Close Long',
  'Market Order Liquidation: Close Short',
  'Liquidated Isolated Long',
  'Liquidated Isolated Short',
  'Auto-Deleveraging',
])
/** HIP-4 Outcome (予測市場) の約定で現れる dir */
const outcomeDirs = new Set([
  'Buy',
  'Sell',
  'Settlement',
  'Split Outcome',
  'Merge Outcome',
])

/**
 * HIP-4 Outcome (予測市場) の約定かを判定する。
 * Outcome の coin は `#<10 * outcome id + side>` 形式 (例: `#12100`)。
 * @param params.coin - 約定の coin
 * @returns Outcome の約定の場合は true
 */
const isOutcomeCoin = ({ coin }: { coin: string }): boolean =>
  coin.startsWith('#')

export const fetchApiData: FetchApiData = async ({
  apiKey,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    createHeaders,
    proxy,
  })

  const { tokens, universe } = await fetchSpotMetaInfo({ client })

  const response = await client.run({
    method: 'POST',
    path: '/info',
    body: {
      type: 'userFillsByTime',
      startTime: from,
      ...(to ? { endTime: to } : {}),
      aggregateByTime: false,
    },
    responseSchema,
  })

  // 昇順ソート by time
  const sortedFills = response.sort((a, b) => a.time - b.time)

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // トレードをoid, timeでグループ化
  const tradeGroups = new Map<string, typeof sortedFills>()

  for (const item of sortedFills) {
    fetchedFrom =
      fetchedFrom === undefined ? item.time : Math.min(fetchedFrom, item.time)
    fetchedTo =
      fetchedTo === undefined ? item.time : Math.max(fetchedTo, item.time)

    const key = `${item.oid}_${item.time}`
    const group = tradeGroups.get(key) ?? []
    group.push(item)
    tradeGroups.set(key, group)
  }

  // グループ毎に処理
  for (const trades of tradeGroups.values()) {
    const firstTrade = trades[0]

    const isOutcome = isOutcomeCoin({ coin: firstTrade.coin })
    if (isOutcome && !outcomeDirs.has(firstTrade.dir)) {
      throw new Error(`Unsupported outcome trade direction: ${firstTrade.dir}`)
    }

    // Spot の場合 (dir が spotDirs)
    if (!isOutcome && spotDirs.has(firstTrade.dir)) {
      let base: string
      let quote: string

      if (firstTrade.coin.includes('/')) {
        ;[base, quote] = firstTrade.coin.split('/')
      } else {
        const item = universe.find((u) => u.name === firstTrade.coin)
        if (!item) {
          throw new AssetNotFoundException(firstTrade.coin)
        }
        const baseToken = tokens.find((t) => t.index === item.tokens[0])
        const quoteToken = tokens.find((t) => t.index === item.tokens[1])
        if (!baseToken || !quoteToken) {
          throw new AssetNotFoundException(firstTrade.coin)
        }
        base = baseToken.name
        quote = quoteToken.name
      }

      const baseAsset = getAsset(base)
      const quoteAsset = getAsset(quote)
      const feeAsset = getAsset(firstTrade.feeToken)
      if (!baseAsset) {
        throw new AssetNotFoundException(base)
      }
      if (!quoteAsset) {
        throw new AssetNotFoundException(quote)
      }
      if (!feeAsset) {
        throw new AssetNotFoundException(firstTrade.feeToken)
      }
      const assets = [baseAsset, quoteAsset, feeAsset]

      // change amount by asset
      const changes = new Map<string, BigDecimal>()
      for (const trade of trades) {
        if (trade.dir === 'Buy') {
          const baseAmount = changes.get(baseAsset.id) ?? createBigDecimal(0n)
          changes.set(baseAsset.id, plus(baseAmount, trade.sz))
          const quoteAmount = changes.get(quoteAsset.id) ?? createBigDecimal(0n)
          changes.set(
            quoteAsset.id,
            minus(quoteAmount, times(trade.sz, trade.px)),
          )
        } else {
          const baseAmount = changes.get(baseAsset.id) ?? createBigDecimal(0n)
          changes.set(baseAsset.id, minus(baseAmount, trade.sz))
          const quoteAmount = changes.get(quoteAsset.id) ?? createBigDecimal(0n)
          changes.set(
            quoteAsset.id,
            plus(quoteAmount, times(trade.sz, trade.px)),
          )
        }
        const feeAmount = changes.get(feeAsset.id) ?? createBigDecimal(0n)
        const totalTradeFee = trade.builderFee
          ? plus(trade.fee, trade.builderFee)
          : trade.fee
        changes.set(feeAsset.id, minus(feeAmount, totalTradeFee))
      }

      const transfers: Transfer[] = []
      for (const [id, amount] of changes.entries()) {
        if (amount.value === 0n) {
          continue
        }
        const asset = assets.find((a) => a.id === id)
        if (!asset) {
          throw new AssetNotFoundException(id)
        }
        transfers.push({
          asset,
          amount: absoluteValue(amount),
          direction: amount.value > 0n ? 'in' : 'out',
        })
      }

      actions.push({
        comment: `Trade ID: ${trades.map((t) => t.tid).join(' / ')}`,
        evidence: 'system-rule',
        order: 0,
        source: `${hyperliquidAccountCategory}_${config.type}_${firstTrade.oid}_${firstTrade.time}`,
        type: 'trade',
        timestamp: firstTrade.time,
        transfers,
      })
    }
    // Perp の場合 (dir が perpDirs)
    // Outcome (予測市場) もトークンを資産として持たず、Hyperliquid が返す確定損益 (closedPnl) と手数料で記録する
    else if (isOutcome || perpDirs.has(firstTrade.dir)) {
      // closedPnl の合計値計算
      let totalClosedPnl = createBigDecimal(0n)
      let totalFee = createBigDecimal(0n)

      for (const trade of trades) {
        if (trade.closedPnl) {
          totalClosedPnl = plus(totalClosedPnl, trade.closedPnl)
        }
        const tradeFee = trade.builderFee
          ? plus(trade.fee, trade.builderFee)
          : trade.fee
        totalFee = plus(totalFee, tradeFee)
      }

      // closedPnl != 0 の場合、realize action 作成
      if (totalClosedPnl.value !== 0n) {
        const asset = getAsset(firstTrade.feeToken)
        if (!asset) {
          throw new AssetNotFoundException(firstTrade.feeToken)
        }

        actions.push({
          comment: `Realized PL of ${firstTrade.coin}/${firstTrade.feeToken}`,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${firstTrade.oid}_${firstTrade.time}`,
          type: totalClosedPnl.value > 0n ? 'realize-profit' : 'realize-loss',
          timestamp: firstTrade.time,
          transfers: [
            {
              asset,
              amount: absoluteValue(totalClosedPnl),
              direction: totalClosedPnl.value > 0n ? 'in' : 'out',
            },
          ],
        })
      }

      // fee != 0 の場合、fee action 作成
      if (totalFee.value !== 0n) {
        const feeAsset = getAsset(firstTrade.feeToken)
        if (!feeAsset) {
          throw new AssetNotFoundException(firstTrade.feeToken)
        }

        actions.push({
          comment: `Trade Fee of ${firstTrade.coin}/${firstTrade.feeToken}`,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${firstTrade.oid}_${firstTrade.time}`,
          type: totalFee.value > 0n ? 'fee' : 'income',
          timestamp: firstTrade.time,
          transfers: [
            {
              asset: feeAsset,
              amount: absoluteValue(totalFee),
              direction: totalFee.value > 0n ? 'out' : 'in',
            },
          ],
        })
      }
    }
    // それ以外の場合は未対応エラー
    else {
      throw new Error(`Unsupported trade direction: ${firstTrade.dir}`)
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions.map((action) =>
      parseHyperliquidActionEvidence(action),
    ),
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
